import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';

const DOW = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS_FULL = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

/** Разбор строки <input type="datetime-local"> ("YYYY-MM-DDTHH:mm") без сдвига часового пояса. */
function parse(s: string): { y: number; mo: number; d: number; h: number; mi: number } | null {
  if (!s) return null;
  const [date, time] = s.split('T');
  const [y, mo, d] = (date ?? '').split('-').map(Number);
  const [h, mi] = (time ?? '00:00').split(':').map(Number);
  if (!y || !mo || !d) return null;
  return { y, mo, d, h: h || 0, mi: mi || 0 };
}

const pad = (n: number) => String(n).padStart(2, '0');
const format = (s: string) => {
  const p = parse(s);
  return p ? `${pad(p.d)}.${pad(p.mo)}.${p.y} ${pad(p.h)}:${pad(p.mi)}` : '';
};
const toValue = (y: number, mo: number, d: number, h: number, mi: number) =>
  `${y}-${pad(mo)}-${pad(d)}T${pad(h)}:${pad(mi)}`;

/**
 * Свой попап выбора даты/времени — замена нативного <input type="datetime-local">, у которого
 * календарь рисует сам браузер и стилизовать его нельзя. Принимает и отдаёт ТУ ЖЕ строку
 * ("YYYY-MM-DDTHH:mm"), что и нативный инпут — drop-in замена без переделки dtIn/dtOut.
 *
 * Попап рендерится через портал в document.body и позиционируется по координатам кнопки
 * (getBoundingClientRect), а не через CSS absolute/fixed внутри родителя: если компонент
 * открыт внутри модалки с анимацией (CSS transform на родителе — animate-pop и т.п.), то
 * position:fixed подложки начинает отсчитываться от ЭТОГО родителя вместо экрана целиком
 * (так работает спецификация CSS) — тогда подложка-клик-вне перестаёт ловить клики за
 * пределами модалки, и попап невозможно закрыть мимо явного выбора. Портал решает это
 * полностью: контент всегда в самом верху дерева, никакой родитель не вмешивается.
 */
export function DateTimePicker({ value, onChange, placeholder = 'ДД.ММ.ГГГГ --:--', className }: {
  value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const p = parse(value);
  const now = new Date();
  const [viewY, setViewY] = useState(p?.y ?? now.getFullYear());
  const [viewMo, setViewMo] = useState(p?.mo ?? now.getMonth() + 1);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Открыли попап — показываем месяц выбранной даты (или текущий, если пусто) и считаем
  // позицию под кнопкой, с учётом нижнего края экрана (если снизу не влезает — открываем вверх).
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    setViewY(p?.y ?? now.getFullYear());
    setViewMo(p?.mo ?? now.getMonth() + 1);
    const r = btnRef.current.getBoundingClientRect();
    const PANEL_H = 420;
    const openUp = r.bottom + PANEL_H > window.innerHeight && r.top > PANEL_H;
    setPos({
      top: openUp ? r.top - 6 : r.bottom + 6,
      left: Math.min(r.left, window.innerWidth - 300 - 8),
      width: r.width,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Закрытие по клику вне кнопки/попапа и по Escape. Слушаем на document, а не через
  // полноэкранную подложку-div — та ломается внутри трансформированных родителей (см. выше).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shiftMonth = (dir: -1 | 1) => {
    let m = viewMo + dir, y = viewY;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setViewMo(m); setViewY(y);
  };

  const pickDay = (d: number) => {
    const h = p?.h ?? 0, mi = p?.mi ?? 0;
    onChange(toValue(viewY, viewMo, d, h, mi));
  };
  const setTime = (h: number, mi: number) => {
    const y = p?.y ?? viewY, mo = p?.mo ?? viewMo, d = p?.d ?? 1;
    onChange(toValue(y, mo, d, ((h % 24) + 24) % 24, ((mi % 60) + 60) % 60));
  };
  const goToday = () => {
    const t = new Date();
    onChange(toValue(t.getFullYear(), t.getMonth() + 1, t.getDate(), p?.h ?? t.getHours(), p?.mi ?? t.getMinutes()));
    setViewY(t.getFullYear()); setViewMo(t.getMonth() + 1);
  };

  // Сетка дней месяца, неделя с понедельника.
  const firstDow = (new Date(viewY, viewMo - 1, 1).getDay() + 6) % 7; // 0=Пн
  const daysInMonth = new Date(viewY, viewMo, 0).getDate();
  const cells: (number | null)[] = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (cells.length % 7 !== 0) cells.push(null);
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);

  return (
    <>
      <button ref={btnRef} type="button" onClick={() => setOpen((v) => !v)}
        className={cn('input flex items-center justify-between gap-2 text-left', !value && 'text-ink-3', className)}>
        <span className="truncate">{value ? format(value) : placeholder}</span>
        <CalendarIcon className="w-4 h-4 text-ink-3 flex-shrink-0" />
      </button>

      {open && createPortal(
        <div ref={panelRef} className="fixed z-[70] panel p-3 w-[300px] shadow-pop animate-pop"
          style={{ top: pos.top, left: pos.left }}>
          {/* Месяц */}
          <div className="flex items-center justify-between mb-2">
            <button type="button" className="icon-btn !w-7 !h-7" onClick={() => shiftMonth(-1)}><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-semibold text-ink-1">{MONTHS_FULL[viewMo - 1]} {viewY}</span>
            <button type="button" className="icon-btn !w-7 !h-7" onClick={() => shiftMonth(1)}><ChevronRight className="w-4 h-4" /></button>
          </div>

          {/* Дни недели */}
          <div className="grid grid-cols-7 mb-1">
            {DOW.map((d) => (
              <div key={d} className="h-6 grid place-items-center text-[10px] uppercase text-ink-3 font-semibold">{d}</div>
            ))}
          </div>

          {/* Сетка дней */}
          <div className="grid grid-cols-7 gap-0.5 mb-3">
            {cells.map((d, i) => {
              if (d == null) return <div key={i} />;
              const cellDate = new Date(viewY, viewMo - 1, d);
              const isToday = cellDate.getTime() === today0.getTime();
              const isSelected = p?.y === viewY && p?.mo === viewMo && p?.d === d;
              return (
                <button key={i} type="button" onClick={() => pickDay(d)}
                  className={cn(
                    'h-8 rounded-lg text-sm grid place-items-center transition-colors',
                    isSelected ? 'bg-accent text-white font-semibold'
                      : isToday ? 'text-accent font-semibold bg-accent-soft/40 hover:bg-accent-soft/60'
                        : 'text-ink-1 hover:bg-surface-2',
                  )}>
                  {d}
                </button>
              );
            })}
          </div>

          {/* Время */}
          <div className="flex items-center justify-center gap-3 mb-3 pt-2 border-t border-line">
            <TimeStepper value={p?.h ?? 0} max={23} onChange={(h) => setTime(h, p?.mi ?? 0)} />
            <span className="text-ink-3 font-semibold">:</span>
            <TimeStepper value={p?.mi ?? 0} max={59} onChange={(mi) => setTime(p?.h ?? 0, mi)} />
          </div>

          {/* Действия */}
          <div className="flex items-center justify-between">
            <button type="button" className="text-xs font-medium text-ink-3 hover:text-ink-1 transition-colors"
              onClick={() => { onChange(''); setOpen(false); }}>Очистить</button>
            <button type="button" className="text-xs font-medium text-accent hover:text-accent/80 transition-colors"
              onClick={goToday}>Сегодня</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/** Колонка часов/минут: стрелка вверх, число (можно вписать вручную), стрелка вниз. */
function TimeStepper({ value, max, onChange }: { value: number; max: number; onChange: (v: number) => void }) {
  const [text, setText] = useState(pad(value));
  useEffect(() => setText(pad(value)), [value]);
  const commit = (raw: string) => {
    const n = Number(raw.replace(/\D/g, ''));
    if (Number.isFinite(n)) onChange(((n % (max + 1)) + (max + 1)) % (max + 1));
    else setText(pad(value));
  };
  return (
    <div className="flex flex-col items-center gap-0.5">
      <button type="button" className="icon-btn !w-7 !h-6" onClick={() => onChange(value + 1 > max ? 0 : value + 1)}>
        <ChevronUp className="w-3.5 h-3.5" />
      </button>
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => commit(text)}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className="w-9 h-8 rounded-lg bg-surface-2 border border-line text-center text-sm font-semibold text-ink-1 tabular-nums focus:border-accent focus:outline-none"
        inputMode="numeric"
      />
      <button type="button" className="icon-btn !w-7 !h-6" onClick={() => onChange(value - 1 < 0 ? max : value - 1)}>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
